import type {
  ContactHandleSchema,
  JurisdictionType,
  PoliceDepartmentEnrichmentStatus,
} from '@/services/api/generated/policeDepartmentsService';
import { TimeZone } from '@/services/types/firms-types';

import {
  getEnrichmentStatusConfig,
  getJurisdictionTypeConfig,
} from './policeFormatters';

const STATE_CODE_MAP: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

const TIMEZONE_LABEL_MAP: Record<TimeZone, string> = {
  [TimeZone.PST]: 'Pacific (PST)',
  [TimeZone.MST]: 'Mountain (MST)',
  [TimeZone.CST]: 'Central (CST)',
  [TimeZone.EST]: 'Eastern (EST)',
  [TimeZone.AKST]: 'Alaska (AKST)',
  [TimeZone.AST]: 'Atlantic (AST)',
  [TimeZone.HST]: 'Hawaii (HST)',
};

export function formatJurisdictionType(type: JurisdictionType): string {
  const config = getJurisdictionTypeConfig(type);
  return config.label;
}

export function formatEnrichmentStatus(
  status: PoliceDepartmentEnrichmentStatus,
): { label: string; color: string } {
  const config = getEnrichmentStatusConfig(status);
  return {
    label: config.label,
    color: config.badgeColor.bg,
  };
}

export function formatTimeZone(tz: TimeZone): string {
  return TIMEZONE_LABEL_MAP[tz] || tz;
}

export function getStateFullName(stateCode: string): string {
  const upperCode = stateCode.toUpperCase();
  return STATE_CODE_MAP[upperCode] || stateCode;
}

export function formatContactHandle(handle: ContactHandleSchema): string {
  const type = handle.handle_type?.toUpperCase() || 'CONTACT';
  const value = handle.value || '';

  if (!value) {
    return type;
  }

  switch (type) {
    case 'PHONE':
    case 'FAX':
      return formatPhoneNumber(value);
    case 'EMAIL':
      return value;
    case 'WEBSITE':
    case 'URL':
      return formatUrl(value);
    default:
      return `${type}: ${value}`;
  }
}

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return phone;
}

function formatUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
